import { inject, Injectable, signal, computed } from '@angular/core';
import { Params } from '@angular/router';
import {
  ContextModalParams,
  ContextModalType
} from '@portal/app/dashboard/context-modal/context-model.types';
import { environment } from '@portal/environments/environment';
import { ContextStore } from '@portal/app/shared/state/context.store';
import { DataResponse, Filter } from '@portal/app/shared/types';
import {
  DateTimeService,
  DayJsDateFormat
} from '@portal/app/shared/services/date-time.service';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { SelectionService } from '@portal/app/shared/services/selection.service';
import { ActivatedRoute } from '@angular/router';
import dayjs from 'dayjs';

export const DATE_FORMAT = 'MMM DD, YYYY';

@Injectable()
export class ModalService {
  private selection = inject(SelectionService);
  private contextStore = inject(ContextStore);
  private dateTimeService = inject(DateTimeService);
  private route = inject(ActivatedRoute);

  public static baseUriV1 = `${environment.apiDomain}/api/v1/bff/xcd/context-menu`;

  private conversionTypeSignal = signal<string>('');
  private modalTypeSignal = signal<ContextModalType>(ContextModalType.unknown);
  private returnCurveViewTypeSignal = signal<'salesI' | 'ordersI'>('ordersI');
  private comparePreviousPeriodSignal = signal<string>('');
  private compareCurrentPeriodSignal = signal<string>('');
  private modalParamsSignal = signal<ContextModalParams>(
    {} as ContextModalParams
  );

  private currentRowSignal = signal<ContextModalParams>(
    {} as ContextModalParams
  );

  // Convert query params to signal - automatically manages subscription lifecycle
  private queryParamSignal = toSignal(this.route.queryParams);

  public modalParams$ = toObservable(this.modalParams);

  constructor() {
    this.contextStore.filterContext.subscribe((filters: Filter[]) => {
      this.setConversionTypeFromFilter(filters);
    });
  }

  private setConversionTypeFromFilter(filters: Filter[]) {
    const value = filters.find((f) => f.literalId === 'conversion_type')?.value;
    if (value) {
      this.conversionTypeSignal.set(value.toString());
    }
  }

  public updateConversionTypeFromParams(params: Params): void {
    if (params.conversion_type) {
      try {
        const decoded = decodeURIComponent(params.conversion_type);
        this.conversionTypeSignal.set(decoded);
      } catch (e) {
        console.error('Error decoding conversion_type:', e);
        this.conversionTypeSignal.set(params.conversion_type);
      }
    }
  }

  get conversionType() {
    return this.conversionTypeSignal.asReadonly();
  }

  get modalType() {
    return this.modalTypeSignal.asReadonly();
  }

  get returnCurveViewType() {
    return this.returnCurveViewTypeSignal.asReadonly();
  }

  get comparePreviousPeriod() {
    return this.comparePreviousPeriodSignal.asReadonly();
  }

  get compareCurrentPeriod() {
    return this.compareCurrentPeriodSignal.asReadonly();
  }

  get modalParams() {
    return this.modalParamsSignal.asReadonly();
  }

  get currentRow() {
    return this.currentRowSignal.asReadonly();
  }

  // Computed signal that derives isSpendLevel from query params
  get isSpendLevel() {
    return computed(() => {
      const params = this.queryParamSignal();
      if (!params?.isSpendLevel) {
        return false;
      }
      const decoded = this.getDecodedURIParam(params, 'isSpendLevel');
      return decoded === 'true';
    });
  }

  setCurrentRow(row: DataResponse | ContextModalParams) {
    const {
      channel,
      tactic,
      dateStart,
      dateStop,
      previousDateStart,
      previousDateStop
    } = row;
    const currentRow = {
      channel: channel as string,
      tactic: tactic as string,
      dateStart: dateStart as string,
      dateStop: dateStop as string,
      previousDateStart: previousDateStart as string,
      previousDateStop: previousDateStop as string
    };
    this.currentRowSignal.set(currentRow);
    this.setModalParams(currentRow);
  }

  setModalParams(params: ContextModalParams) {
    if (params.tactic) {
      this.modalTypeSignal.set(ContextModalType.tactic);
    } else {
      this.modalTypeSignal.set(ContextModalType.channel);
    }
    this.modalParamsSignal.set(params);
    this.compareCurrentPeriodSignal.set(
      `${dayjs(params.dateStart).format(DATE_FORMAT)} - ${dayjs(params.dateStop).format(DATE_FORMAT)}`
    );
    this.comparePreviousPeriodSignal.set(
      `${dayjs(params.previousDateStart).format(DATE_FORMAT)} - ${dayjs(params.previousDateStop).format(DATE_FORMAT)}`
    );
  }

  getParams(): Params {
    const { clientId, brandId } = this.selection.getClientIdAndBrandId();
    return { clientId, brandId };
  }

  getLast30DaysDateParams() {
    const dateParams: Params = {};
    const currentMonth = this.dateTimeService.calculatePrevious30Days();
    const previousMonth =
      this.dateTimeService.calculatePreviousPeriod(currentMonth);
    dateParams.dateStart = currentMonth.startDate.format(
      DayJsDateFormat.fullDate
    );
    dateParams.dateStop = currentMonth.endDate.format(DayJsDateFormat.fullDate);
    dateParams.previousDateStart = previousMonth.startDate.format(
      DayJsDateFormat.fullDate
    );
    dateParams.previousDateStop = previousMonth.endDate.format(
      DayJsDateFormat.fullDate
    );
    return dateParams;
  }

  getDecodedURIParam(params: Params, metric: string) {
    return params[metric] ? decodeURIComponent(params[metric]) : '';
  }
}
